import { z } from "zod";

export const RegisterSchema = z.object({
  ippis: z
    .string()
    .min(6, { message: "IPPIS must be at least 6 digits" })
    .max(6, { message: "IPPIS must be 6 digits" }),
  bvn: z.string().min(11, { message: "BVN must be 11 digits" }).max(11, { message: "BVN must be 11 digits" }),
  accountNumber: z
    .string()
    .min(10, { message: "Account number must be 10 digits" })
    .max(10, { message: "Account number must be 10 digits" }),
});

export const PassWordSchema = z
  .object({
    password: z
      .string()
      .min(8, { message: "Password must be at least 8 characters" })
      .max(16, { message: "Password must be less than 16 characters" })
      .regex(/^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,50}$/, {
        message: "Password must contain one number and one special character",
      }),
    confirmPassword: z.string().min(8, { message: "Password must be at least 8 characters" }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

export const EmailSchema = z.object({
  email: z.string().min(1, { message: "Email is required" }).email({ message: "Please enter a valid email address" }),
});
// todo: update this schema for when user can now login with ippis, email or phone number
export const LoginSchema = z.object({
  ippis: z.string().min(1, { message: "IPPIS number is required" }),
  password: z.string().min(1, { message: "Password is required" }),
});

export const UserLenderFormSchema = z.object({
  lenderId: z.string().min(1, { message: "Lender is required" }),
  termsAndConditions: z.boolean().refine((data) => data, { message: "You must accept the terms and conditions" }),
});

export const BillingSchema = z.object({
  // fullName: z.string().min(1, { message: "Full name is required" }),
  // phoneNumber: z
  //   .string()
  //   .min(11, { message: "Phone number must be at least 11 digits" })
  //   .max(14, { message: "Phone number must be less than 14 digits" })
  //   .regex(/^[+]?[0-9]+$/, { message: "Phone number can only contain numbers and optional + prefix" }),
  paid: z.boolean().refine((data) => data, { message: "Payment method is required" }),
});

export const ReviewProductSchema = z.object({
  rating: z.number().min(1, { message: "Rating is required" }).max(5, { message: "Rating must be between 1 and 5" }),
  review: z.string().optional(),
});

export const ReturnRequestSchema = z.object({
  reason: z.string().min(1, { message: "Reason is required" }),
});

export const AddressSchema = z.object({
  firstName: z.string().min(1, { message: "First name is required" }),
  lastName: z.string().min(1, { message: "Last name is required" }),
  phoneNumber: z
    .string()
    .min(11, { message: "Phone number must be at least 11 digits" })
    .max(14, { message: "Phone number must be less than 14 digits" })
    .regex(/^[+]?[0-9]+$/, { message: "Phone number can only contain numbers and optional + prefix" }),
  additionalPhoneNumber: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val.length === 0) return true;
        return /^[+]?[0-9]+$/.test(val) && val.length >= 11 && val.length <= 14;
      },
      { message: "Phone number can only contain numbers and optional + prefix" }
    ),
  deliveryAddress: z.string().min(10, { message: "Address must be at least 10 characters" }),
  state: z.string().min(1, { message: "State is required" }),
  lga: z.string().min(1, { message: "LGA is required" }),
  setAsDefault: z.boolean().optional(),
});

export const ChangePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, { message: "Current password is required" }),
    newPassword: z
      .string()
      .min(8, { message: "Password must be at least 8 characters" })
      .regex(/[A-Z]/, { message: "Password must contain at least one uppercase letter" })
      .regex(/[a-z]/, { message: "Password must contain at least one lowercase letter" })
      .regex(/[0-9]/, { message: "Password must contain at least one number" })
      .regex(/[!@#$%^&*(),.?":{}|<>]/, { message: "Password must contain at least one special character" }),
    confirmPassword: z.string().min(1, { message: "Please confirm your password" }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

export const ForgotPasswordIdentifierSchema = z.object({
  identifier: z.string().min(1, { message: "Identifier is required" }),
});

export const ForgotPasswordCodeSchema = z.object({
  code: z.string().min(1, { message: "Code is required" }),
});

export const SalesAgentApplicationSchema = z.object({
  firstName: z.string().min(1, { message: "First name is required" }),
  lastName: z.string().min(1, { message: "Last name is required" }),
  age: z
    .number({ invalid_type_error: "Age must be a number" })
    .min(18, { message: "You must be at least 18 years old" })
    .max(100, { message: "Please enter a valid age" }),
  phoneNumber: z
    .string()
    .min(11, { message: "Phone number must be at least 11 digits" })
    .max(14, { message: "Phone number must be less than 14 digits" })
    .regex(/^[+]?[0-9]+$/, { message: "Phone number can only contain numbers and optional + prefix" }),
  email: z.string().min(1, { message: "Email is required" }).email({ message: "Please enter a valid email address" }),
  state: z.string().min(1, { message: "State is required" }),
  lga: z.string().min(1, { message: "LGA is required" }),
  houseAddress: z.string().min(10, { message: "Address must be at least 10 characters" }),
  cv: z
    .instanceof(File, { message: "CV is required" })
    .refine((file) => file.size <= 5 * 1024 * 1024, { message: "File size must be less than 5MB" })
    .refine(
      (file) =>
        [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ].includes(file.type),
      { message: "Only PDF, DOC, and DOCX files are accepted" }
    ),
  applicationReason: z.string().min(20, { message: "Please provide at least 20 characters explaining your reason" }),
});

export const ContactFormSchema = z.object({
  fullName: z.string().min(1, { message: "Full name is required" }),
  email: z.string().min(1, { message: "Email is required" }).email({ message: "Please enter a valid email address" }),
  phoneNumber: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val.length === 0) return true;
        return /^[+]?[0-9]+$/.test(val) && val.length >= 11 && val.length <= 14;
      },
      { message: "Please enter a valid phone number" }
    ),
  subject: z.string().min(1, { message: "Please select a subject" }),
  message: z.string().min(10, { message: "Message must be at least 10 characters" }),
  attachment: z
    .any()
    .optional()
    .refine(
      (file) => {
        if (!file || !(file instanceof File)) return true;
        return file.size <= 5 * 1024 * 1024;
      },
      { message: "File size must be less than 5MB" }
    )
    .refine(
      (file) => {
        if (!file || !(file instanceof File)) return true;
        return ["image/jpeg", "image/png", "application/pdf"].includes(file.type);
      },
      { message: "Only JPG, PNG, and PDF files are accepted" }
    ),
});

export type RegisterType = z.infer<typeof RegisterSchema>;
export type PassWordType = z.infer<typeof PassWordSchema>;
export type EmailType = z.infer<typeof EmailSchema>;
export type LoginType = z.infer<typeof LoginSchema>;
export type UserLenderFormType = z.infer<typeof UserLenderFormSchema>;
export type BillingType = z.infer<typeof BillingSchema>;
export type ReviewProductType = z.infer<typeof ReviewProductSchema>;
export type ReturnRequestType = z.infer<typeof ReturnRequestSchema>;
export type AddressType = z.infer<typeof AddressSchema>;
export type ChangePasswordType = z.infer<typeof ChangePasswordSchema>;
export type ForgotPasswordIdentifierType = z.infer<typeof ForgotPasswordIdentifierSchema>;
export type ForgotPasswordCodeType = z.infer<typeof ForgotPasswordCodeSchema>;
export type SalesAgentApplicationType = z.infer<typeof SalesAgentApplicationSchema>;
export type ContactFormType = z.infer<typeof ContactFormSchema>;
